import { useSignupModalStyles as usePartnerSignupStyles } from '@/auth/components/shared/useSignupModalStyles';
import Button from '@/components/Button';

import { STEP_COPY } from '../constants';
import type { useSelfServiceSMBSignupFlow } from '../hooks/useSelfServiceSMBSignupFlow';
import { useStyles as useTeamRequestSentStyles } from './TeamRequestSentStep.styles';

interface TeamRequestSentStepProps {
  flow: ReturnType<typeof useSelfServiceSMBSignupFlow>;
  onGoToDashboard: () => void;
  ctaLabel?: string;
}

// Terminal step for the team-join flow. Renders two distinct success states:
//
//   - joinAutoApproved=true  → user matched an existing email invitation;
//     they're already an active member. Show "you're in" copy.
//   - joinAutoApproved=false → request is awaiting team-owner approval.
//     Show "request sent" copy.
//
// Also serves as the resume target when the viewer returns with an
// outstanding awaiting_approval row (viewer.pendingTeamMembership); in that
// case joinAutoApproved defaults to false from INITIAL_STATE and the
// awaiting copy renders.
export function TeamRequestSentStep({
  flow,
  onGoToDashboard,
  ctaLabel = STEP_COPY.confirmation.ctaDashboard,
}: TeamRequestSentStepProps): JSX.Element {
  const s = usePartnerSignupStyles();
  const styles = useTeamRequestSentStyles();
  const teamName = flow.state.selectedMatch?.name ?? 'your team';
  const autoApproved = flow.state.joinAutoApproved;

  const title = autoApproved ? (
    <>
      You&apos;ve been added to <strong>{teamName}</strong>!
    </>
  ) : (
    'Request sent — waiting on approval'
  );
  const message = autoApproved ? (
    <>Someone on the team invited you earlier. You&apos;re in!</>
  ) : (
    <>
      We sent your request to <strong>{teamName}</strong>. A team owner needs to approve you before
      you can access the workspace.
    </>
  );

  return (
    <>
      <div className={`${s.formContent} ${styles.content}`}>
        <header>
          <h2 id="self-service-smb-team-title" className={styles.title}>
            {title}
          </h2>
          <p className={styles.subtitle}>{message}</p>
        </header>
      </div>
      <div className={s.footer}>
        <span />
        <Button
          color="teal"
          size="normal"
          onClick={onGoToDashboard}
          classes={{ root: s.continueButton }}
        >
          {ctaLabel}
        </Button>
      </div>
    </>
  );
}
